import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Fragment, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import {
  filtrosParaBusca,
  filtrosRelatorioSchema,
  FORMA_PAGAMENTO_ROTULO,
  formatarDataHora,
  formatarReais,
  type FiltrosRelatorio,
} from "@caixaflow/shared";
import { api } from "../api/cliente";
import { useSessao } from "../api/sessao";
import type { Canceladas, ListaOfertas, OpcoesFiltro, Pagina, Perdas, Relatorio, VendaRelatorio } from "../api/tipos";
import { Carregando, FalhaAoCarregar } from "../components/Estados";
import { PainelFiltros } from "../components/FiltrosRelatorio";
import { ModalVenda } from "../components/ModalVenda";
import { ModalOferta } from "../components/Ofertas";
import { LinhaVenda } from "./Vendas";

const FILTROS_PADRAO: FiltrosRelatorio = { status: "VALIDA" };

/** Lê os filtros da URL; valores inválidos são ignorados em vez de quebrar a tela. */
function filtrosDaUrl(parametros: URLSearchParams): FiltrosRelatorio {
  const r = filtrosRelatorioSchema.safeParse(Object.fromEntries(parametros));
  return r.success ? r.data : FILTROS_PADRAO;
}

export function PaginaFechamento() {
  const { data: sessao } = useSessao();
  const souAdmin = sessao?.usuario.perfil === "ADMIN";
  const [parametros, setParametros] = useSearchParams();
  // O estado é a fonte da verdade e a URL só espelha. Assim mudanças rápidas em sequência
  // (escolher evento e já tocar em "Pix") se acumulam em vez de uma apagar a outra.
  const [filtros, setFiltros] = useState<FiltrosRelatorio>(() => filtrosDaUrl(parametros));
  const busca = filtrosParaBusca(filtros);
  const [pagina, setPagina] = useState(1);
  const [vendaAberta, setVendaAberta] = useState<string | null>(null);
  const [ofertaAberta, setOfertaAberta] = useState<string | null>(null);

  useEffect(() => {
    if (busca !== parametros.toString()) setParametros(new URLSearchParams(busca), { replace: true });
  }, [busca, parametros, setParametros]);

  const definir = (mudanca: Partial<FiltrosRelatorio>) => {
    setPagina(1);
    setFiltros((atual) => ({ ...atual, ...mudanca }));
  };

  const opcoes = useQuery({
    queryKey: ["relatorio-opcoes", filtros.evento?.join(",") ?? ""],
    queryFn: () => api<OpcoesFiltro>(`/relatorios/opcoes${filtros.evento?.length ? `?evento=${filtros.evento.join(",")}` : ""}`),
    placeholderData: keepPreviousData,
  });
  const relatorio = useQuery({
    queryKey: ["relatorio", busca],
    queryFn: () => api<Relatorio>(`/relatorios/resumo?${busca}`),
    placeholderData: keepPreviousData,
  });
  const vendas = useQuery({
    queryKey: ["relatorio-vendas", busca, pagina],
    queryFn: () => api<Pagina<VendaRelatorio>>(`/relatorios/vendas?${busca}&pagina=${pagina}&porPagina=25`),
    placeholderData: keepPreviousData,
  });

  // Ofertas seguem os mesmos filtros (a forma de pagamento não se aplica a elas).
  const ofertas = useQuery({
    queryKey: ["relatorio-ofertas", busca],
    queryFn: () => api<ListaOfertas>(`/ofertas?${busca}`),
    placeholderData: keepPreviousData,
  });

  // Canceladas aparecem sempre (como o indicador), seja qual for a situação escolhida.
  const canceladas = useQuery({
    queryKey: ["relatorio-canceladas", busca],
    queryFn: () => api<Canceladas>(`/relatorios/canceladas?${busca}`),
    placeholderData: keepPreviousData,
  });
  const perdas = useQuery({
    queryKey: ["relatorio-perdas", busca],
    queryFn: () => api<Perdas>(`/relatorios/perdas?${busca}`),
    placeholderData: keepPreviousData,
    enabled: souAdmin,
  });

  const r = relatorio.data;
  const o = ofertas.data;
  const p = perdas.data;
  const buscaCanceladas = filtrosParaBusca({ ...filtros, status: "CANCELADA" });
  const totalPaginas = vendas.data ? Math.max(1, Math.ceil(vendas.data.total / vendas.data.porPagina)) : 1;

  return (
    <>
      <PainelFiltros
        filtros={filtros}
        opcoes={opcoes.data}
        souAdmin={souAdmin}
        definir={definir}
        limpar={() => {
          setPagina(1);
          setFiltros(FILTROS_PADRAO);
        }}
      />

      <div className="linha">
        <span className="mudo pequeno">
          {r && (r.periodo.de ? `Vendas de ${formatarDataHora(r.periodo.de)} a ${formatarDataHora(r.periodo.ate!)}` : "Nenhuma venda nestes filtros")}
          {relatorio.isFetching && " · atualizando…"}
        </span>
        <div className="linha-esq">
          <a className="btn pequeno fantasma" href={`/api/relatorios/vendas.csv?${busca}`} download>
            Baixar planilha (CSV)
          </a>
          <a className="btn pequeno primario" href={`/api/relatorios/relatorio.pdf?${busca}`} download>
            Baixar PDF
          </a>
          <a className="btn pequeno fantasma" href={`/api/relatorios/relatorio.pdf?${busca}${busca ? "&" : ""}comVendas=1`} download>
            PDF com vendas
          </a>
        </div>
      </div>

      {relatorio.isPending && <Carregando />}
      {relatorio.error && <FalhaAoCarregar erro={relatorio.error} tentarDeNovo={() => relatorio.refetch()} />}
      {r && (
        <>
          <div className="kpis">
            <div className="kpi hl">
              <span>Total arrecadado</span>
              <b>{formatarReais(r.indicadores.totalCentavos)}</b>
            </div>
            <div className="kpi">
              <span>Vendas</span>
              <b>{r.indicadores.vendas}</b>
            </div>
            <div className="kpi">
              <span>Itens vendidos</span>
              <b>{r.indicadores.itens}</b>
            </div>
            <div className="kpi">
              <span>Ticket médio</span>
              <b>{formatarReais(r.indicadores.ticketMedioCentavos)}</b>
            </div>
            <div className="kpi">
              <span>Canceladas</span>
              <b>{r.indicadores.canceladas.quantidade}</b>
              <span className="pequeno" style={{ textTransform: "none", letterSpacing: 0 }}>
                {formatarReais(r.indicadores.canceladas.totalCentavos)}
              </span>
            </div>
            {souAdmin && (
              <div className="kpi">
                <span>Perdas</span>
                <b>{p ? `${p.quantidade} itens` : "…"}</b>
                <span className="pequeno" style={{ textTransform: "none", letterSpacing: 0 }}>
                  {p ? `${formatarReais(p.valorReferenciaCentavos)} de referência` : ""}
                </span>
              </div>
            )}
            <div className="kpi">
              <span>Ofertas</span>
              <b>{o ? `${o.resumo.itens} itens` : "…"}</b>
              <span className="pequeno" style={{ textTransform: "none", letterSpacing: 0 }}>
                {o ? `${formatarReais(o.resumo.valorReferenciaCentavos)} de referência` : ""}
              </span>
            </div>
          </div>

          <section>
            <h2>Por forma de pagamento</h2>
            <div className="card tbl-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Forma</th>
                    <th className="r">Vendas</th>
                    <th className="r">Valor</th>
                    <th className="r">%</th>
                  </tr>
                </thead>
                <tbody>
                  {r.porPagamento.map((p) => (
                    <tr key={p.formaPagamento}>
                      <td>{FORMA_PAGAMENTO_ROTULO[p.formaPagamento]}</td>
                      <td className="r">{p.vendas}</td>
                      <td className="r">{formatarReais(p.totalCentavos)}</td>
                      <td className="r">{p.percentual.toFixed(1).replace(".", ",")}%</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td>Total</td>
                    <td className="r">{r.indicadores.vendas}</td>
                    <td className="r">{formatarReais(r.indicadores.totalCentavos)}</td>
                    <td className="r">{r.indicadores.totalCentavos ? "100%" : "0%"}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>

          <section>
            <h2>Por categoria e item</h2>
            {r.porCategoria.length === 0 ? (
              <div className="card vazio">Nenhum item nestes filtros.</div>
            ) : (
              <div className="card tbl-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th className="r">Qtd</th>
                      <th className="r">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.porCategoria.map((c) => (
                      <Fragment key={`${c.categoriaId}-${c.categoria}`}>
                        <tr className="cat">
                          <td>{c.categoria}</td>
                          <td className="r">{c.quantidade}</td>
                          <td className="r">{formatarReais(c.totalCentavos)}</td>
                        </tr>
                        {c.itens.map((i) => (
                          <tr key={`${i.produtoId}-${i.nome}`}>
                            <td className="quebra">{i.nome}</td>
                            <td className="r">{i.quantidade}</td>
                            <td className="r">{formatarReais(i.totalCentavos)}</td>
                          </tr>
                        ))}
                      </Fragment>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td>Total</td>
                      <td className="r">{r.indicadores.itens}</td>
                      <td className="r">{formatarReais(r.indicadores.totalCentavos)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </section>

          {souAdmin && (
            <section>
              <h2>Por operador</h2>
              {r.porOperador.length === 0 ? (
                <div className="card vazio">Nenhuma venda nestes filtros.</div>
              ) : (
                <div className="card tbl-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Operador</th>
                        <th className="r">Vendas</th>
                        <th className="r">Itens</th>
                        <th className="r">Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {r.porOperador.map((o) => (
                        <tr key={o.operadorId}>
                          <td>{o.nome}</td>
                          <td className="r">{o.vendas}</td>
                          <td className="r">{o.itens}</td>
                          <td className="r">{formatarReais(o.totalCentavos)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}

          <section>
            <h2>Por caixa</h2>
            {r.porCaixa.length === 0 ? (
              <div className="card vazio">Nenhuma venda nestes filtros.</div>
            ) : (
              <div className="card tbl-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Caixa</th>
                      <th>Evento</th>
                      {souAdmin && <th>Operador</th>}
                      <th>Abertura</th>
                      <th className="r">Vendas</th>
                      <th className="r">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.porCaixa.map((c) => (
                      <tr key={c.caixaId}>
                        <td>
                          <Link to={`/caixas/${c.caixaId}`}>#{c.numero}</Link>{" "}
                          {c.status === "ABERTO" && <span className="chip ok">aberto</span>}
                          {c.origem === "IMPORTACAO" && <span className="chip alerta">importado</span>}
                        </td>
                        <td className="quebra">{c.evento}</td>
                        {souAdmin && <td>{c.operador}</td>}
                        <td className="mudo">{formatarDataHora(c.abertoEm)}</td>
                        <td className="r">{c.vendas}</td>
                        <td className="r">{formatarReais(c.totalCentavos)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}

      <section>
        <h2>Vendas canceladas{canceladas.data ? ` (${canceladas.data.total})` : ""}</h2>
        {canceladas.error && <FalhaAoCarregar erro={canceladas.error} tentarDeNovo={() => canceladas.refetch()} />}
        {canceladas.data?.total === 0 && <div className="card vazio">Nenhuma venda cancelada nestes filtros.</div>}
        {!!canceladas.data?.itens.length && (
          <div className="card">
            {canceladas.data.itens.map((v) => (
              <div key={v.id} className="venda-linha">
                <span className="t">{formatarDataHora(v.criadaEm)}</span>
                <span className="d">
                  #{v.numero} · {v.itens.map((i) => `${i.quantidade}× ${i.nomeProduto}`).join(", ")}
                  <span className="chip">{FORMA_PAGAMENTO_ROTULO[v.formaPagamento]}</span>
                  <div className="mudo pequeno">
                    Caixa #{v.caixa}
                    {souAdmin && ` · ${v.operador}`} · cancelada em {formatarDataHora(v.canceladaEm)}: {v.motivo}
                    {v.autorizadoPor && ` · autorizado por ${v.autorizadoPor}`}
                    {v.solicitadoPor && v.solicitadoPor !== v.autorizadoPor && ` (pedido por ${v.solicitadoPor})`}
                  </div>
                </span>
                <span className="v">{formatarReais(v.totalCentavos)}</span>
                <button className="link-botao" onClick={() => setVendaAberta(v.id)} aria-label={`Ver venda #${v.numero}`}>
                  Ver
                </button>
              </div>
            ))}
          </div>
        )}
        {canceladas.data && canceladas.data.total > canceladas.data.itens.length && (
          <Link className="link-botao" style={{ paddingLeft: 0 }} to={`/vendas?${buscaCanceladas}`}>
            Ver todas as {canceladas.data.total} canceladas →
          </Link>
        )}
      </section>

      {souAdmin && (
        <section>
          <h2>Perdas{p ? ` (${p.total})` : ""}</h2>
          {!!(filtros.operador?.length || filtros.caixa?.length || filtros.pagamento?.length) && (
            <p className="mudo pequeno" style={{ marginTop: 0 }}>
              Os filtros de operador, caixa e pagamento não se aplicam às perdas.
            </p>
          )}
          {perdas.error && <FalhaAoCarregar erro={perdas.error} tentarDeNovo={() => perdas.refetch()} />}
          {p?.total === 0 && <div className="card vazio">Nenhuma perda nestes filtros.</div>}
          {!!p?.itens.length && (
            <div className="card tbl-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Quando</th>
                    <th>Produto</th>
                    <th className="r">Qtd</th>
                    <th>Motivo</th>
                    <th>Lançada por</th>
                    <th className="r">Valor de referência</th>
                  </tr>
                </thead>
                <tbody>
                  {p.itens.map((x) => (
                    <tr key={x.id}>
                      <td className="mudo">{formatarDataHora(x.criadoEm)}</td>
                      <td className="quebra">{x.produto}</td>
                      <td className="r">{x.quantidade}</td>
                      <td className="quebra">{x.motivo ?? "—"}</td>
                      <td>{x.lancadaPor}</td>
                      <td className="r">{formatarReais(x.valorReferenciaCentavos)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={2}>Total perdido</td>
                    <td className="r">{p.quantidade}</td>
                    <td colSpan={2} />
                    <td className="r">{formatarReais(p.valorReferenciaCentavos)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
          <p className="mudo pequeno">Valor de referência pelo preço atual do produto. Perdas não entram no caixa: só reduzem o estoque.</p>
        </section>
      )}

      <section>
        <div className="linha" style={{ marginBottom: 8 }}>
          <h2 style={{ margin: 0 }}>Ofertas</h2>
          <a className="btn pequeno fantasma" href={`/api/relatorios/ofertas.csv?${busca}`} download>
            Baixar ofertas (CSV)
          </a>
        </div>
        {filtros.pagamento?.length ? (
          <p className="mudo pequeno" style={{ marginTop: 0 }}>
            O filtro de forma de pagamento não se aplica às ofertas (nada foi pago).
          </p>
        ) : null}
        {ofertas.error && <FalhaAoCarregar erro={ofertas.error} tentarDeNovo={() => ofertas.refetch()} />}
        {o && o.itens.length === 0 && <div className="card vazio">Nenhuma oferta nestes filtros.</div>}
        {o && o.itens.length > 0 && (
          <>
            <div className="card tbl-wrap" style={{ marginBottom: 10 }}>
              <table>
                <thead>
                  <tr>
                    <th>Pedido de</th>
                    <th className="r">Ofertas</th>
                    <th className="r">Itens</th>
                    <th className="r">Valor de referência</th>
                  </tr>
                </thead>
                <tbody>
                  {o.resumo.porSolicitante.map((s) => (
                    <tr key={s.solicitadoPor}>
                      <td className="quebra">{s.solicitadoPor}</td>
                      <td className="r">{s.ofertas}</td>
                      <td className="r">{s.itens}</td>
                      <td className="r">{formatarReais(s.valorReferenciaCentavos)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="card">
              {o.itens.map((of) => (
                <div key={of.id} className={`venda-linha${of.status === "CANCELADA" ? " cancelada" : ""}`}>
                  <span className="t">{formatarDataHora(of.criadaEm)}</span>
                  <span className="d">
                    #{of.numero} · {of.itens.map((i) => `${i.quantidade}× ${i.nomeProduto}`).join(", ")}
                    {of.status === "CANCELADA" && <span className="chip perigo">cancelada</span>}
                    <div className="mudo pequeno">
                      Pedido de {of.solicitadoPor} · {of.caixa ? `Caixa #${of.caixa.numero}` : "pelo estoque"} · {of.registradaPor.nome}
                    </div>
                  </span>
                  <span className="v mudo">{formatarReais(of.valorReferenciaCentavos)}</span>
                  <button className="link-botao" onClick={() => setOfertaAberta(of.id)} aria-label={`Ver oferta #${of.numero}`}>
                    Ver
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      <section>
        <h2>Vendas</h2>
        {vendas.error && <FalhaAoCarregar erro={vendas.error} tentarDeNovo={() => vendas.refetch()} />}
        {vendas.data?.itens.length === 0 && <div className="card vazio">Nenhuma venda nestes filtros.</div>}
        {!!vendas.data?.itens.length && (
          <div className="card">
            {vendas.data.itens.map((v) => (
              <LinhaVenda key={v.id} venda={v} mostrarOperador={souAdmin} onAbrir={() => setVendaAberta(v.id)} />
            ))}
          </div>
        )}
        {totalPaginas > 1 && (
          <div className="linha" style={{ marginTop: 10 }}>
            <button className="btn pequeno fantasma" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>
              Mais recentes
            </button>
            <span className="mudo pequeno">
              Página {pagina} de {totalPaginas} · {vendas.data?.total} vendas
            </span>
            <button className="btn pequeno fantasma" disabled={pagina >= totalPaginas} onClick={() => setPagina((p) => p + 1)}>
              Anteriores
            </button>
          </div>
        )}
      </section>

      <ModalVenda vendaId={vendaAberta} onFechar={() => setVendaAberta(null)} />
      <ModalOferta ofertaId={ofertaAberta} onFechar={() => setOfertaAberta(null)} />
    </>
  );
}
